// Cliente del Banco CMF (saldos, movimientos, transferencias). El backend
// (webadmin2) invoca internamente la Lambda CMFConnectorProd vía IAM.
//
// ⚠️ Formato numérico: CMF es inconsistente entre endpoints.
//   - ConsultarSaldos → coma decimal AR ("1.250.340,75")
//   - ConsultarMovimientos → punto decimal ("-2.00")
// `parseMonto` normaliza ambos a number.

const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ??
  "http://localhost:3000";

function authHeader(): Record<string, string> {
  const jwt = localStorage.getItem("webadmin2.jwt");
  return jwt ? { Authorization: `Bearer ${jwt}` } : {};
}

async function authFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const res = await fetch(url, init);
  if (res.status === 401) {
    localStorage.removeItem("webadmin2.jwt");
    localStorage.removeItem("webadmin2.email");
    if (typeof window !== "undefined") window.location.replace("/login");
  }
  return res;
}

async function jsonOrThrow(res: Response, label: string): Promise<any> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error ?? `${label}: HTTP ${res.status}`);
  return data;
}

/** Normaliza un monto que puede venir como "1.250.340,75" (AR) o "-2.00" (US). */
export function parseMonto(v: string | number | null | undefined): number {
  if (v == null) return 0;
  if (typeof v === "number") return v;
  const s = v.trim();
  if (!s) return 0;
  // Si tiene coma, es formato AR: el punto es separador de miles.
  if (s.includes(",")) return Number(s.replace(/\./g, "").replace(",", ".")) || 0;
  return Number(s) || 0;
}

export function fmtMonto(n: number): string {
  return n.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ---------- Cuentas CMF conocidas ----------
// Qué cuentas del banco son "nuestras" y cómo las llamamos. La lista vive en el
// backend (GET /cmf/cuentas: banco + etiquetas guardadas en la tabla settings +
// seed de la recaudadora) y se consume vía el hook useCmfAccounts()
// (services/cmfAccounts.ts). Acá sólo quedan el default offline y los llamados.
export interface CmfCuenta {
  numero:      string;
  label:       string | null;
  hist:        boolean;                       // true = el job la indexa (Histórico disponible)
  source:      "seed" | "banco" | "manual";   // manual = etiquetada pero el banco no la devuelve
  en_banco?:   boolean | null;                // null = el banco no respondió
  updated_by?: string | null;
  updated_at?: string | null;
}
export const CMF_CUENTA_DEFAULT = "30054402";   // recaudadora (CBU 3190001830000300544029)
export const CMF_CUENTA_RE = /^\d{6,12}$/;

export async function getCmfCuentas(): Promise<{ cuentas: CmfCuenta[]; banco_disponible: boolean }> {
  const res = await authFetch(`${API_BASE}/cmf/cuentas`, { headers: authHeader() });
  return jsonOrThrow(res, "getCmfCuentas");
}
/** Guarda la etiqueta operativa de una cuenta. Vacío = borra la etiqueta. */
export async function setCmfCuentaLabel(numero: string, label: string): Promise<CmfCuenta> {
  const res = await authFetch(`${API_BASE}/cmf/cuentas/${encodeURIComponent(numero)}`, {
    method: "PUT", headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify({ label }),
  });
  return jsonOrThrow(res, "setCmfCuentaLabel");
}

// ---------- Saldos ----------
export interface CuentaSaldo {
  tipo_cuenta:   string;
  numero_cuenta: string;
  moneda:        string;   // "PESOS" | "DÓLARES"
  cbu:           string;
  saldos: { real: string; disponible: string; apertura: string };
}

export async function getSaldos(): Promise<CuentaSaldo[]> {
  const res = await authFetch(`${API_BASE}/cmf/saldos`, { headers: authHeader() });
  const data = await jsonOrThrow(res, "getSaldos");
  return (data.cuentas ?? []) as CuentaSaldo[];
}

// ---------- Movimientos ----------
export interface Movimiento {
  fecha_hora:    string;
  fecha_valor:   string;
  concepto:      string;
  detalle:       string | null;
  originante:    { tipo_documento?: string; nro_documento?: string } | null;
  comprobante:   string;
  moneda:        string;
  monto:         string;
  saldo_parcial: string;
}

export interface MovimientosResult {
  movimientos:   Movimiento[];
  total_paginas: number;
}

// ---------- Movimientos: histórico persistido ----------
// Copia completa que el backend indexa en DynamoDB (tabla cmf_movimientos).
// A diferencia de getMovimientos (live contra el connector), esto lee el
// histórico ya guardado: Query por cuenta + rango, rápido y sin rate-limit.
export interface MovimientoHist extends Movimiento {
  cuenta:     string;
  fecha:      string;   // día YYYY-MM-DD
  sort_key:   string;
  indexed_at: string;
}

export interface MovimientosHistResult {
  movimientos: MovimientoHist[];
  count:       number;
  next_cursor: string | null;
}

export async function getMovimientosHistory(params: {
  cuenta:       string;
  from?:        string;   // YYYY-MM-DD
  to?:          string;   // YYYY-MM-DD
  order?:       "asc" | "desc";
  limit?:       number;
  cursor?:      string | null;
}): Promise<MovimientosHistResult> {
  const qs = new URLSearchParams({ cuenta: params.cuenta });
  if (params.from)   qs.set("from",   params.from);
  if (params.to)     qs.set("to",     params.to);
  if (params.order)  qs.set("order",  params.order);
  if (params.limit)  qs.set("limit",  String(params.limit));
  if (params.cursor) qs.set("cursor", params.cursor);
  const res = await authFetch(`${API_BASE}/cmf/movimientos/history?${qs}`, {
    headers: authHeader(),
  });
  const data = await jsonOrThrow(res, "getMovimientosHistory");
  return {
    movimientos: (data.movimientos ?? []) as MovimientoHist[],
    count:       Number(data.count ?? 0),
    next_cursor: (data.next_cursor ?? null) as string | null,
  };
}

/** Export asíncrono para rangos que superan el tope del export directo (50k
 *  filas): el backend arma el CSV completo y manda un mail al usuario logueado
 *  con un link de descarga (válido 6 hs). Devuelve el email de destino. */
export async function requestMovimientosExport(params: {
  cuenta: string;
  from?:  string;   // YYYY-MM-DD
  to?:    string;   // YYYY-MM-DD
}): Promise<{ email: string }> {
  const res = await authFetch(`${API_BASE}/cmf/movimientos/export`, {
    method:  "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body:    JSON.stringify(params),
  });
  const data = await jsonOrThrow(res, "requestMovimientosExport");
  return { email: String(data.email ?? "") };
}

// Paginación server-side: una página por request. CMF devuelve 10/página y no
// respeta el page size, así que pedimos de a una y el usuario navega.
export async function getMovimientos(params: {
  numero_cuenta: string;
  fecha_desde:   string;   // YYYY-MM-DD
  fecha_hasta:   string;
  pagina?:       number;
}): Promise<MovimientosResult> {
  const res = await authFetch(`${API_BASE}/cmf/movimientos`, {
    method:  "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body:    JSON.stringify({
      numero_cuenta: params.numero_cuenta,
      fecha_desde:   params.fecha_desde,
      fecha_hasta:   params.fecha_hasta,
      pagina:        String(params.pagina ?? 1),
    }),
  });
  const data = await jsonOrThrow(res, "getMovimientos");
  return {
    movimientos:   (data.movimientos ?? []) as Movimiento[],
    total_paginas: Number(data.total_paginas ?? 1),
  };
}

// La transferencia (write) se removió a pedido: webadmin2 expone CMF solo lectura
// (saldos / movimientos). Si se reincorpora, ver historial de git.

// ---------- Recupero de webhooks Coelsa (/cmf/recupero) ----------
// Cruce del día: créditos por DEBIN en la cuenta recaudadora (CMF) vs lo que el
// core registró en received_payments (por id de Coelsa). Lo que está en CMF y no
// en el core es un AvisoCVU que Coelsa no entregó (típicamente por un corte del
// túnel). Reinyectar = publicar en la cola del core el mismo mensaje que el
// webhook real; el consumidor deduplica por id.
export interface RecuperoFaltante {
  id: string;
  fecha_hora: string;          // ART, del movimiento CMF
  monto: number;
  concepto: string;
  comprobante: string;
  cuit_pagador: string;
  cuenta_origen: string;       // pagador real (BuscarTransferencia)
  cvu: string;                 // CVU destino (colector)
  cuit_destino: string;
  titular: string;
  estado_cmf: string;          // "ACREDITADO" esperado
  reinyectable: boolean;
  motivo?: string;             // por qué no es reinyectable
}
export interface RecuperoBuscarResult {
  day: string;                 // YYYY-MM-DD (ART)
  from: string;                // HH:MM
  to: string;                  // HH:MM
  page_from: number;
  page_to: number;
  total_paginas: number | null;
  next_page: number | null;    // null = terminó
  movimientos_leidos: number;
  creditos_en_ventana: number;
  registrados: number;
  faltantes: RecuperoFaltante[];
}
export async function buscarRecupero(params: { from: string; to: string; page?: number }): Promise<RecuperoBuscarResult> {
  const res = await authFetch(`${API_BASE}/cmf/recupero/buscar`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify(params),
  });
  return jsonOrThrow(res, "buscarRecupero");
}
export interface RecuperoReinyeccion {
  id: string;
  ok: boolean;
  resultado: "enviado" | "ya_registrado" | "rechazado" | "error";
  detalle: string;
  message_id?: string;
}
export async function reinyectarRecupero(items: { id: string; cuit_pagador?: string }[]): Promise<{ resultados: RecuperoReinyeccion[] }> {
  const res = await authFetch(`${API_BASE}/cmf/recupero/reinyectar`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify({ items }),
  });
  return jsonOrThrow(res, "reinyectarRecupero");
}

// ---------- Modificar CVU en Coelsa (/cmf/modificar-cvu) ----------
// Escritura en Coelsa vía CoelsaConnectorProd (POST /api/coelsa/ModificarCvu →
// PUT /apiCVU/CVU/ModifCVU/{cvu}/{cuit}). Sin parámetro ambiente: el conector
// invocado es el de producción. Spec y códigos: docs/coelsa-cvu-modificar.md.
export interface ModificarCvuDatos {
  cvu: string;                 // 22 dígitos (en el path del PUT)
  cuit: string;                // 11 dígitos, titular de la CVU (en el path)
  cta_id: number;              // ID de la cuenta recaudadora en Coelsa
  tipo: "1";                   // único valor válido
  titular: string;
  moneda: "032";               // pesos, único valor válido
  persona_tipo: "F" | "J";
}
export interface ModificarCvuResult {
  ok: boolean;                 // HTTP 2xx y respuesta.codigo === "3300"
  status: number;              // HTTP del conector
  codigo: string | null;       // código Coelsa 33xx si vino
  descripcion: string | null;  // de Coelsa, o de la tabla de códigos
  resumen: string;
  enviado: { banco: string; datos: ModificarCvuDatos };
  respuesta: unknown;          // JSON de Coelsa, o texto plano si el conector falló
}
export async function modificarCvu(datos: ModificarCvuDatos): Promise<ModificarCvuResult> {
  const res = await authFetch(`${API_BASE}/cmf/modificar-cvu`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify(datos),
  });
  return jsonOrThrow(res, "modificarCvu");
}
